// SWR hook for the editor-entered STV results state.
//
// Mirrors the existing electionday hooks:
//   Public-side: fetches /api/electionday-state?kind=results. The
//   endpoint reads from Postgres and is edge-cached (s-maxage=10,
//   stale-while-revalidate=30), so reader polls on counting night
//   collapse into ~one DB read per 10 s per region.
//
//   Admin-side: pass `admin: true` to read via the authenticated
//   endpoint with no caching, so the editor always sees their own
//   writes immediately.

import useSWR, { type SWRConfiguration } from 'swr';
import {
  emptyResultsState,
  type ResultsState,
} from '../data/electionday-results-types';

const PUBLIC_URL = '/api/electionday-state?kind=results';
const ADMIN_URL = '/api/admin/electionday-results';

const fetcher = async (url: string): Promise<ResultsState> => {
  const res = await fetch(url, { cache: 'no-store', credentials: 'include' });
  if (res.status === 404) return emptyResultsState();
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as ResultsState;
};

export interface UseResultsOptions {
  admin?: boolean;
  refreshInterval?: number;
}

export function useElectionDayResults(
  options: UseResultsOptions = {},
): {
  data: ResultsState;
  isLoading: boolean;
  error: unknown;
  mutate: () => Promise<unknown>;
} {
  const url = options.admin ? ADMIN_URL : PUBLIC_URL;
  const config: SWRConfiguration = {
    refreshInterval: options.refreshInterval ?? 12_000,
    revalidateOnFocus: true,
    errorRetryCount: 2,
  };
  const { data, isLoading, error, mutate } = useSWR<ResultsState>(
    url,
    fetcher,
    config,
  );
  return {
    data: data ?? emptyResultsState(),
    isLoading,
    error,
    mutate,
  };
}
